import { SmartMeterGatewayModel } from '../model'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { fetchSmartMeterGateway, updateSmartMeterGateway } from '../api'
import { SmartMeterGatewayFormModel } from '../transformers/from-smart-meter-gateway-form-to-dto.transformer'
import { usePortalMutation, usePortalQuery } from 'src/Libs/api'
import { useQueryClient } from '@tanstack/react-query'
import { trustSmartMeterGatewaySki } from 'src/Libs/installation-settings/api/smart-meter-gateway.api'
import { TrustSmgwSkiForm } from 'src/Libs/installation-settings/model/smart-meter-gateway.model'

interface SmartMeterGatewayHook {
  isLoading: boolean
  isUpdating: boolean
  smartMeterGatewayData?: SmartMeterGatewayModel
  error?: Error | AuthFetchError
  updateSmartMeterGatewayData: ({ storageId, value }: UpdateSmartMeterGatewayVariables) => Promise<void | null>
  refetchSmartMeterGatewayData: () => Promise<unknown>
  trustSmartMeterGatewaySki: (form: TrustSmgwSkiForm) => Promise<void | null>
  isTrusting: boolean
}

interface UpdateSmartMeterGatewayVariables {
  storageId: string
  value: SmartMeterGatewayFormModel
}

export const smartMeterGatewayQueryKey = (storageId: string | undefined) => ['smart-meter-gateway', storageId] as const

/**
 * Hook handles smart-meter-gateway-data by fetching data from BE and return the data or error
 */
const useSmartMeterGatewayData = (storageId?: string, isEnabled = true): SmartMeterGatewayHook => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()

  const {
    data: smartMeterGatewayData,
    isLoading,
    error,
    refetch,
  } = usePortalQuery({
    queryKey: smartMeterGatewayQueryKey(storageId),
    queryFn: () => fetchSmartMeterGateway(storageId, authFetch),
    enabled: !!storageId && isEnabled,
  })

  const { mutateAsync: updateSmartMeterGatewayMutation, isPending: isUpdating } = usePortalMutation<
    void,
    Error | AuthFetchError,
    UpdateSmartMeterGatewayVariables
  >({
    mutationFn: ({ storageId, value }) => updateSmartMeterGateway(storageId, value, authFetch),
    onSuccess: (_data, { storageId }) => queryClient.invalidateQueries({ queryKey: smartMeterGatewayQueryKey(storageId) }),
  })

  const { mutateAsync: trustSmartMeterGatewaySkiMutation, isPending: isTrusting } = usePortalMutation<
    void,
    Error | AuthFetchError,
    { storageId: string | undefined; skiToTrust: string }
  >({
    mutationFn: ({ storageId, skiToTrust }) => trustSmartMeterGatewaySki(storageId, skiToTrust, authFetch),
    onSuccess: (_data, { storageId }) => queryClient.invalidateQueries({ queryKey: smartMeterGatewayQueryKey(storageId) }),
  })

  return {
    isLoading,
    isUpdating,
    smartMeterGatewayData: smartMeterGatewayData ?? undefined,
    error: error as Error | AuthFetchError | undefined,
    updateSmartMeterGatewayData: updateSmartMeterGatewayMutation,
    refetchSmartMeterGatewayData: refetch,
    trustSmartMeterGatewaySki: trustSmartMeterGatewaySkiMutation,
    isTrusting,
  }
}

export default useSmartMeterGatewayData
